import { useCallback, useState } from 'react';
import { makeStyles, mergeClasses, tokens, Button, Spinner, Text } from '@fluentui/react-components';
import { DismissFilled, NavigationFilled } from '@fluentui/react-icons';
import { OttoLogo } from '@/components/OttoLogo';
import { EXTERNAL_LINKS, MOBILE_BREAKPOINT } from './constants';

const useStyles = makeStyles({
  skipLink: {
    position: 'absolute',
    top: '-40px',
    left: '0',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    paddingBlock: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    zIndex: 100,
    fontWeight: tokens.fontWeightSemibold,
    borderRadius: tokens.borderRadiusMedium,
    ':focus': {
      top: tokens.spacingVerticalS,
      left: tokens.spacingHorizontalS,
    },
  },
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 50,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottomStyle: 'solid',
    borderBottomWidth: '1px',
    borderBottomColor: tokens.colorNeutralStroke2,
    backdropFilter: 'blur(12px)',
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: '1200px',
    marginInline: 'auto',
    paddingBlock: tokens.spacingVerticalM,
    paddingInline: tokens.spacingHorizontalXXL,
    gap: tokens.spacingHorizontalL,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      paddingInline: tokens.spacingHorizontalL,
    },
  },
  brandLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    textDecorationLine: 'none',
    color: tokens.colorNeutralForeground1,
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusSmall,
    },
  },
  brandText: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '-0.01em',
  },
  navLinks: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXL,
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      display: 'none',
    },
  },
  navLink: {
    color: tokens.colorNeutralForeground2,
    textDecorationLine: 'none',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    transitionDuration: tokens.durationNormal,
    transitionTimingFunction: tokens.curveEasyEase,
    transitionProperty: 'color',
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '2px',
      borderRadius: tokens.borderRadiusSmall,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  navCtas: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  signInGhost: {
    [`@media (max-width: 540px)`]: {
      display: 'none',
    },
  },
  primaryCta: {
    fontWeight: tokens.fontWeightSemibold,
  },
  hamburger: {
    display: 'none',
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      display: 'inline-flex',
    },
  },
  mobileMenu: {
    display: 'none',
    [`@media (max-width: ${MOBILE_BREAKPOINT})`]: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalXS,
      paddingBlock: tokens.spacingVerticalL,
      paddingInline: tokens.spacingHorizontalXXL,
      backgroundColor: tokens.colorNeutralBackground2,
      borderBottomStyle: 'solid',
      borderBottomWidth: '1px',
      borderBottomColor: tokens.colorNeutralStroke2,
    },
  },
  mobileLink: {
    color: tokens.colorNeutralForeground2,
    textDecorationLine: 'none',
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightMedium,
    paddingBlock: tokens.spacingVerticalS,
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '2px',
    },
  },
  mobileCta: {
    marginBlockStart: tokens.spacingVerticalS,
  },
});

interface LandingNavProps {
  onLogin: (source?: string) => void;
  /** True while MSAL is verifying the silent session. Disables the
   *  sign-in / get-started buttons and shows a spinner so the user
   *  doesn't fire an interactive `loginRedirect` before silent SSO has
   *  had a chance. */
  isVerifyingAuth?: boolean;
}

interface NavItem {
  readonly label: string;
  readonly href: string;
}

// In-page anchors that resolve to sections rendered further down the
// landing page. We intentionally don't include "Docs" or "Pricing"
// here — those targets aren't published yet (see TODOs in constants).
const NAV_ITEMS: readonly NavItem[] = [
  { label: 'Product', href: '#product' },
  { label: 'Use cases', href: '#use-cases' },
  { label: 'Enterprise', href: '#enterprise' },
];

export const LandingNav = ({ onLogin, isVerifyingAuth = false }: LandingNavProps) => {
  const styles = useStyles();
  const [mobileOpen, setMobileOpen] = useState(false);
  const toggleMobile = useCallback(() => setMobileOpen((v) => !v), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const primaryCtaIcon = isVerifyingAuth ? <Spinner size="tiny" /> : undefined;
  const primaryCtaLabel = isVerifyingAuth ? 'Verifying access…' : 'Get started';
  const signInLabel = isVerifyingAuth ? 'Verifying access…' : 'Sign in';

  return (
    <>
      <a href="#main-content" className={styles.skipLink}>
        Skip to content
      </a>
      <header className={styles.header}>
        <nav className={styles.nav} aria-label="Main navigation">
          <a href={EXTERNAL_LINKS.auto} className={styles.brandLink} aria-label="Logic Apps Automation home">
            <OttoLogo size={24} />
            <Text className={styles.brandText}>Logic Apps Automation</Text>
          </a>
          <div className={styles.navLinks}>
            {NAV_ITEMS.map((item) => (
              <a key={item.href} href={item.href} className={styles.navLink}>
                {item.label}
              </a>
            ))}
          </div>
          <div className={styles.navCtas}>
            <Button
              appearance="subtle"
              size="small"
              onClick={() => onLogin('nav-signin')}
              disabled={isVerifyingAuth}
              aria-busy={isVerifyingAuth}
              className={styles.signInGhost}
            >
              {signInLabel}
            </Button>
            <Button
              appearance="primary"
              size="small"
              onClick={() => onLogin('nav-primary')}
              disabled={isVerifyingAuth}
              aria-busy={isVerifyingAuth}
              className={styles.primaryCta}
              {...(primaryCtaIcon ? { icon: primaryCtaIcon } : {})}
            >
              {primaryCtaLabel}
            </Button>
            <Button
              className={styles.hamburger}
              appearance="subtle"
              icon={mobileOpen ? <DismissFilled /> : <NavigationFilled />}
              onClick={toggleMobile}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav-menu"
            />
          </div>
        </nav>
        {mobileOpen && (
          <nav id="mobile-nav-menu" className={styles.mobileMenu} aria-label="Mobile navigation">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={styles.mobileLink}
                onClick={closeMobile}
              >
                {item.label}
              </a>
            ))}
            <Button
              className={mergeClasses(styles.primaryCta, styles.mobileCta)}
              appearance="primary"
              size="small"
              onClick={() => { closeMobile(); onLogin('mobile-menu'); }}
              disabled={isVerifyingAuth}
              aria-busy={isVerifyingAuth}
              {...(primaryCtaIcon ? { icon: primaryCtaIcon } : {})}
            >
              {primaryCtaLabel}
            </Button>
          </nav>
        )}
      </header>
    </>
  );
};
